// This tab's rolling copy of a room's message frames, kept in sessionStorage per event and viewer so
// a reload or a discarded tab reopens on the conversation. The session bootstrap's recent messages
// (LiveChat::RecentMessages, read from the room's CloudWatch log) lag the room by the log's delivery
// time and never include a shadow-banned viewer's own echoes; the buffer covers both. Storage can be
// full, blocked or missing, so every access may fail and the buffer then lives in memory only.

const KEY_PREFIX = "live_chat:";
const KEY_SUFFIX = ":messages";
const MAX_FRAMES = 100;
const MAX_REMOVED = 200;
// Frames can arrive at the room's full rate, so writes are coalesced and flushed on the way out.
const SAVE_DELAY_MS = 500;

// The id the chat panel gives a shadow-banned viewer's local echo (see its simulateEcho).
export const SHADOW_ID_PREFIX = "shadow-";

export function isShadowEcho(frame) {
  return String(frame.id).startsWith(SHADOW_ID_PREFIX);
}

function isMessageFrame(frame) {
  return !!frame && frame.type === "message" && frame.id != null && typeof frame.at === "string"
    && !Number.isNaN(Date.parse(frame.at)) && !!frame.user && typeof frame.user === "object";
}

// Deduped on the IVS id and on the sender's client_msg_id, like the panel's rows; the first copy
// wins, so a server frame listed ahead of a buffered one replaces it.
function latest(frames) {
  const ids = new Set();
  const clientIds = new Set();
  const unique = frames.filter((frame) => {
    if (!isMessageFrame(frame)) return false;
    const id = String(frame.id);
    const clientId = frame.client_msg_id != null ? String(frame.client_msg_id) : null;
    if (ids.has(id) || (clientId !== null && clientIds.has(clientId))) return false;
    ids.add(id);
    if (clientId !== null) clientIds.add(clientId);
    return true;
  });
  return unique.sort((a, b) => Date.parse(a.at) - Date.parse(b.at)).slice(-MAX_FRAMES);
}

export class HistoryBuffer {
  constructor({ contextType, contextId, viewerId }) {
    this.viewerId = String(viewerId);
    this.key = `${KEY_PREFIX}${contextType}:${contextId}:${this.viewerId}${KEY_SUFFIX}`;
    this.dropOtherKeys();
    const { frames, removed } = this.load();
    this.frames = frames;
    // Every message id this tab has heard deleted, oldest first.
    this.removed = removed;
    this.saveTimer = null;

    this.onPageHide = () => this.flush();
    this.onVisibilityChange = () => {
      if (document.visibilityState === "hidden") this.flush();
    };
    window.addEventListener("pagehide", this.onPageHide);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  // Oldest first.
  get messages() {
    return this.frames.slice();
  }

  // The session's list is the room as its log had it through `through`, moderation applied, so it
  // replaces this tab's copy up to then. Kept from the buffer: what the log has not delivered yet,
  // and the viewer's own shadow echoes, which never reach it. A delete this tab already heard beats
  // a cached list that predates it.
  merge(serverFrames, through) {
    const cutoff = through ? Date.parse(through) : NaN;
    const server = serverFrames.filter((frame) => !this.removed.has(String(frame.id)));
    const kept = Number.isNaN(cutoff)
      ? this.frames
      : this.frames.filter((frame) => isShadowEcho(frame) || Date.parse(frame.at) > cutoff);
    this.frames = latest([...server, ...kept]);
    this.save();
  }

  append(frame) {
    if (!isMessageFrame(frame) || this.removed.has(String(frame.id)) || this.includes(frame)) return;
    // The panel places the buffer in one ordered pass. Live frames arrive in send order, so they are
    // pushed; one the panel made (a shadow echo, a resolved send) can land after a newer one and
    // takes the full sort.
    const last = this.frames[this.frames.length - 1];
    if (last && Date.parse(frame.at) < Date.parse(last.at)) {
      this.frames = latest([...this.frames, frame]);
    } else {
      this.frames.push(frame);
      if (this.frames.length > MAX_FRAMES) this.frames.splice(0, this.frames.length - MAX_FRAMES);
    }
    this.save();
  }

  remove(messageId) {
    if (messageId == null) return;
    const id = String(messageId);
    this.removed.delete(id);
    this.removed.add(id);
    while (this.removed.size > MAX_REMOVED) this.removed.delete(this.removed.values().next().value);
    this.frames = this.frames.filter((frame) => String(frame.id) !== id);
    this.save();
  }

  removeUser(userId) {
    this.retain((frame) => String(frame.user.id) !== String(userId));
  }

  dispose() {
    this.flush();
    window.removeEventListener("pagehide", this.onPageHide);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  // --- internals ------------------------------------------------------------

  includes(frame) {
    const clientId = frame.client_msg_id != null ? String(frame.client_msg_id) : null;
    return this.frames.some((existing) => String(existing.id) === String(frame.id)
      || (clientId !== null && existing.client_msg_id != null && String(existing.client_msg_id) === clientId));
  }

  retain(keep) {
    const frames = this.frames.filter(keep);
    if (frames.length === this.frames.length) return;
    this.frames = frames;
    this.save();
  }

  // A tab watches one room at a time, so an earlier event's copy (or another account's) would only
  // spend the origin's storage quota, which the rest of the app shares.
  dropOtherKeys() {
    try {
      const storage = window.sessionStorage;
      for (let index = storage.length - 1; index >= 0; index -= 1) {
        const key = storage.key(index);
        if (key && key !== this.key && key.startsWith(KEY_PREFIX) && key.endsWith(KEY_SUFFIX)) storage.removeItem(key);
      }
    } catch (error) {
      // Storage unavailable: there is nothing to drop.
    }
  }

  load() {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(this.key) || "{}");
      const frames = stored && Array.isArray(stored.frames) ? latest(stored.frames) : [];
      const removed = stored && Array.isArray(stored.removed) ? stored.removed.map(String).slice(-MAX_REMOVED) : [];
      return { frames, removed: new Set(removed) };
    } catch (error) {
      return { frames: [], removed: new Set() };
    }
  }

  save() {
    if (this.saveTimer) return;
    this.saveTimer = window.setTimeout(() => this.flush(), SAVE_DELAY_MS);
  }

  // A no-op unless a save is pending.
  flush() {
    if (!this.saveTimer) return;
    window.clearTimeout(this.saveTimer);
    this.saveTimer = null;
    try {
      window.sessionStorage.setItem(this.key, JSON.stringify({ frames: this.frames, removed: Array.from(this.removed) }));
    } catch (error) {
      // Full, blocked or unavailable: the buffer carries on in memory.
    }
  }
}
